import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraOverlayDrawer } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Sparkles } from 'lucide-react';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { create } from 'zustand';
import { OfficeDocumentDrawer } from '../../components/ChatView/OfficeDocumentDrawer';
import { AskSession } from '../../components/EvidenceAsk/AskSession';
import { DocumentEvidence } from '../../components/EvidenceAsk/DocumentEvidence';
import { EvidenceAsk } from '../../components/EvidenceAsk/EvidenceAsk';
import { useAuth } from '../../contexts/authContext';
import { OfficeDocumentDrawerProvider } from '../../contexts/OfficeDocumentDrawerContext';
import { assistConversationId } from '../../lib/chat/envelope';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { useCaptureWorkspace } from './hooks';
// Only an ephemeral point-of-need selection. Conversation/history remains in
// the existing assistant harness; this store owns no model or shared facts.
const useCaptureAsk = create((set)=>({
        projectId: '',
        question: '',
        revision: 0,
        open: false,
        setOpen: (open)=>set({
                open
            }),
        consume: (revision)=>set((state)=>state.revision === revision ? {
                    question: ''
                } : {}),
        select: (projectId, question)=>set((state)=>({
                    projectId,
                    question,
                    revision: state.revision + 1,
                    open: true
                }))
    }));
export function useCaptureAi(zouProjectId) {
    const select = useCaptureAsk((state)=>state.select);
    const ask = useCallback((prompt)=>{
        select(zouProjectId, prompt);
    // No navigation: the Evidence drawer opens over the mounted page.
    }, [
        zouProjectId,
        select
    ]);
    return {
        ask,
        isPending: false
    };
}
export function AiAssistButton({ zouProjectId, prompt, label, action, variant = 'outline' }) {
    const { ask } = useCaptureAi(zouProjectId);
    return /*#__PURE__*/ _jsx(SpecraButton, {
        variant: variant === 'ghost' ? 'ghost' : 'secondary',
        size: "sm",
        "data-testid": Testids.capture.evidenceOpen,
        "data-action": action,
        onClick: ()=>ask(prompt),
        iconLeft: /*#__PURE__*/ _jsx(Sparkles, {
            className: "h-3.5 w-3.5"
        }),
        children: label
    });
}
export function AiAssistBar({ zouProjectId }) {
    return /*#__PURE__*/ _jsx(AiAssistButton, {
        zouProjectId: zouProjectId,
        action: "assess",
        label: "Ask",
        prompt: "Assess this pursuit using permitted evidence and identify gaps."
    });
}
/** Mounted by the Capture route, so changing Capture tabs cannot discard the drawer's state. */ export function CaptureEvidenceAsk({ children }) {
    const { id = '' } = useParams();
    const { data } = useCaptureWorkspace(id);
    const open = useCaptureAsk((state)=>state.open);
    const setOpen = useCaptureAsk((state)=>state.setOpen);
    const assistOpen = useLayoutStore((state)=>state.assistOpen);
    const setAssistOpen = useLayoutStore((state)=>state.setAssistOpen);
    const [askFirst, setAskFirst] = useState(false);
    // ChatView's "continue in assist": open the drawer; the region mounts in Ask mode.
    useLayoutEffect(()=>{
        if (assistOpen && !open) {
            setAssistOpen(false);
            setAskFirst(true);
            setOpen(true);
        }
    }, [
        assistOpen,
        open,
        setAssistOpen,
        setOpen
    ]);
    return /*#__PURE__*/ _jsxs(OfficeDocumentDrawerProvider, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "min-w-0",
                children: children
            }),
            open ? /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                open: true,
                onOpenChange: (next)=>{
                    setOpen(next);
                    if (!next) setAskFirst(false);
                },
                title: "Evidence",
                testId: Testids.capture.evidenceDrawer,
                width: "lg",
                children: /*#__PURE__*/ _jsx(CaptureContextRegion, {
                    zouProjectId: data?.summary?.zouProjectId ?? '',
                    initialMode: askFirst ? 'ask' : 'evidence'
                }, id)
            }) : null,
            /*#__PURE__*/ _jsx(OfficeDocumentDrawer, {
                overlay: true
            })
        ]
    });
}
export function CaptureContextRegion({ zouProjectId, initialMode = 'evidence' }) {
    const { id = '' } = useParams();
    const { data, isError, isLoading } = useCaptureWorkspace(id);
    const { user } = useAuth();
    const location = useLocation();
    const selected = useCaptureAsk();
    const [source, setSource] = useState();
    const [mode, setMode] = useState(initialMode);
    const setAssistOrigin = useLayoutStore((state)=>state.setAssistOrigin);
    useLayoutEffect(()=>{
        if (selected.projectId === zouProjectId && selected.question) setMode('ask');
    }, [
        selected.projectId,
        selected.question,
        selected.revision,
        zouProjectId
    ]);
    const envelope = useMemo(()=>({
            surface: 'capture_pursuit',
            pursuitId: id,
            projectId: zouProjectId
        }), [
        id,
        zouProjectId
    ]);
    const chatId = assistConversationId(envelope, user?.id);
    const context = {
        workspace: 'Capture Pursuit',
        record: {
            id,
            label: data?.summary?.name ?? 'Current pursuit'
        },
        version: data?.summary?.updatedAt?.toDate().toISOString() ?? 'Saved record version unavailable',
        source,
        filters: location.search || 'No route filters'
    };
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.capture.aiAssist,
        children: /*#__PURE__*/ _jsx(EvidenceAsk, {
            context: context,
            available: !isError && !isLoading && !!data?.summary,
            mode: mode,
            onModeChange: (next)=>{
                setAssistOrigin(location.pathname);
                setMode(next);
            },
            evidence: /*#__PURE__*/ _jsx("div", {
                className: "space-y-3",
                children: /*#__PURE__*/ _jsx(DocumentEvidence, {
                    onSourceChange: setSource,
                    onAsk: (question)=>selected.select(zouProjectId, question)
                })
            }),
            ask: /*#__PURE__*/ _jsx(AskSession, {
                chatId: chatId,
                envelope: envelope,
                context: context,
                onRequestHandled: selected.consume,
                request: selected.projectId === zouProjectId ? {
                    id: selected.revision,
                    question: selected.question
                } : undefined,
                onClose: ()=>setMode('evidence')
            }, chatId)
        }, id)
    });
}
